import { trace, metrics } from "@opentelemetry/api";
import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import {
  MeterProvider,
  PeriodicExportingMetricReader,
} from "@opentelemetry/sdk-metrics";
import { resourceFromAttributes } from "@opentelemetry/resources";
import { ATTR_SERVICE_NAME } from "@opentelemetry/semantic-conventions";
import { BatchSpanProcessor } from "@opentelemetry/sdk-trace-web";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { OTLPMetricExporter } from "@opentelemetry/exporter-metrics-otlp-http";
import { DocumentLoadInstrumentation } from "@opentelemetry/instrumentation-document-load";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { UserInteractionInstrumentation } from "@opentelemetry/instrumentation-user-interaction";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { reportWebVitals } from "./webVitals";

const OTLP_BASE = import.meta.env.VITE_OTLP_URL ?? "/otlp";

function getSessionId(): string {
  let id = sessionStorage.getItem("otel_session_id");
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem("otel_session_id", id);
  }
  return id;
}

export function initTelemetry() {
  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: "wine-web",
    "session.id": getSessionId(),
  });

  // Auto-instrumentations (always register, even if collector is down)
  const apiUrl = import.meta.env.VITE_API_URL ?? "";
  registerInstrumentations({
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new FetchInstrumentation({
        // Only trace API calls, not static assets or telemetry exports
        ignoreUrls: [/\/otlp\//, /\.(js|css|png|jpg|svg|woff2?)(\?|$)/],
        propagateTraceHeaderCorsUrls: apiUrl
          ? [new RegExp(apiUrl.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))]
          : [],
        clearTimingResources: true,
      }),
      new UserInteractionInstrumentation(),
    ],
  });

  // Probe collector availability before starting exporters to avoid 404 spam
  fetch(`${OTLP_BASE}/v1/traces`, { method: "OPTIONS" }).then(() => {
    startExporters(resource);
  }).catch(() => {
    // Collector unreachable — retry once after 30s
    setTimeout(() => {
      fetch(`${OTLP_BASE}/v1/traces`, { method: "OPTIONS" }).then(() => {
        startExporters(resource);
      }).catch(() => {
        // Collector still down — skip telemetry export silently
      });
    }, 30_000);
  });

  // Web Vitals
  reportWebVitals();
}

function startExporters(resource: ReturnType<typeof resourceFromAttributes>) {
  const traceExporter = new OTLPTraceExporter({
    url: `${OTLP_BASE}/v1/traces`,
  });

  const tracerProvider = new WebTracerProvider({
    resource,
    spanProcessors: [new BatchSpanProcessor(traceExporter)],
  });

  tracerProvider.register();

  const metricExporter = new OTLPMetricExporter({
    url: `${OTLP_BASE}/v1/metrics`,
  });

  const meterProvider = new MeterProvider({
    resource,
    readers: [
      new PeriodicExportingMetricReader({
        exporter: metricExporter,
        exportIntervalMillis: 60_000,
      }),
    ],
  });

  metrics.setGlobalMeterProvider(meterProvider);
}

export function setUserId(userId: string) {
  // Add user.id as a span attribute on all future spans
  const tracer = trace.getTracer("wine-web");
  const span = tracer.startSpan("user.identify");
  span.setAttribute("user.id", userId);
  span.end();

  // Store for use by custom spans
  sessionStorage.setItem("otel_user_id", userId);
}

export function getUserId(): string | null {
  return sessionStorage.getItem("otel_user_id");
}
